import { forwardRef, HTMLAttributes } from 'react';
import { DockableToggle } from 'v5/patterns/sidebar/DockableLayout';
import { SidebarToggle } from 'v5/patterns/sidebar/Sidebar';
import { useDockableLayout } from 'v5/patterns/sidebar/useDockableLayout';

export interface HamburgerMenuProps extends HTMLAttributes<HTMLButtonElement> {
  /**
   * Whether to open the sidebar on hover (default: true)
   */
  openOnHover?: boolean;
}

/**
 * Common hamburger menu button that toggles the sidebar.
 * Only visible when the sidebar is in collapsed mode.
 * Connected to DockableLayout context.
 *
 * @example
 * ```tsx
 * <DockableHeader>
 *   <div className="flex items-center gap-3 px-6 h-12">
 *     <HamburgerMenu />
 *     <h1>Page Title</h1>
 *   </div>
 * </DockableHeader>
 * ```
 */
export const HamburgerMenu = forwardRef<HTMLButtonElement, HamburgerMenuProps>(
  ({ openOnHover = true, ...props }, ref) => {
    const context = useDockableLayout();
    if (!context) return null;
    const { mode } = context;

    // Only show when sidebar is collapsed
    if (mode !== 'collapsed') {
      return null;
    }

    return (
      <div className="contents tailwind-root">
        <DockableToggle openOnHover={openOnHover}>
          <SidebarToggle ref={ref} {...props} />
        </DockableToggle>
      </div>
    );
  }
);

HamburgerMenu.displayName = 'HamburgerMenu';
